import React, { useState, useEffect } from 'react';
import { Package, Calendar, Truck, Check, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';

const Subscriptions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const { currentCurrency } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
    fetchUserSubscription();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const fetchUserSubscription = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*, subscription_plans(*)')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUserSubscription(data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleSubscribe = async (plan: any) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create subscription in Supabase
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .insert([
          {
            user_id: user.id,
            plan_id: plan.id,
            status: 'pending',
            amount: plan.price
          }
        ])
        .select()
        .single();

      if (subscriptionError) throw subscriptionError;

      // For now, we'll just show a success message
      // In production, integrate with a payment provider
      alert('Subscription feature coming soon! We\'ll notify you when it\'s available.');
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process subscription');
      setLoading(false);
    }
  };

  const plans = [
    {
      id: 1,
      name: 'Weekly Delight',
      price: '₹2,499',
      description: 'Perfect for regular mango lovers',
      features: [
        '25kg premium mangoes weekly',
        'Mix of 2-3 varieties',
        'Free delivery',
        'Skip or pause anytime'
      ],
      duration: 'Weekly'
    },
    {
      id: 2,
      name: 'Monthly Feast',
      price: '₹8,999',
      description: 'Best value for families',
      features: [
        '100kg premium mangoes monthly',
        'Mix of 3-4 varieties',
        'Free delivery',
        'Priority harvest selection',
        '10% discount on additional orders'
      ],
      duration: 'Monthly',
      popular: true
    },
    {
      id: 3,
      name: 'Season Special',
      price: '₹16,000',
      description: 'The ultimate mango experience',
      features: [
        '180kg premium mangoes',
        'All varieties included',
        'Free delivery',
        'Priority harvest selection',
        '15% discount on additional orders',
        'Exclusive seasonal gifts'
      ],
      duration: 'Full Season'
    }
  ];

  // If user is from Canada, show message and disable subscriptions
  if (currentCurrency.code === 'CAD') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-4">Subscription Not Available</h1>
            <p className="text-gray-600 mb-6">
              We apologize, but our subscription service is currently only available in India. 
              We're working on bringing this service to Canada soon!
            </p>
            <button
              onClick={() => navigate('/shop-canada')}
              className="bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors"
            >
              View Available Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Mango Subscriptions</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Subscribe to receive regular deliveries of fresh, handpicked mangoes throughout the season.
            Choose the plan that best suits your needs.
          </p>
          <div className="mt-4 bg-yellow-50 text-yellow-800 p-4 rounded-lg inline-block">
            <p className="font-medium">Currently available only in India</p>
          </div>
          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg max-w-md mx-auto">
              {error}
            </div>
          )}
          {userSubscription && (
            <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-lg max-w-md mx-auto">
              You are currently subscribed to the {userSubscription.subscription_plans.name} plan
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-6 w-6 text-yellow-500" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Premium Selection</h3>
            <p className="text-gray-600">
              Handpicked, premium quality mangoes from our orchards
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-6 w-6 text-yellow-500" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Flexible Plans</h3>
            <p className="text-gray-600">
              Choose from weekly, monthly, or seasonal deliveries
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="h-6 w-6 text-yellow-500" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Free Delivery</h3>
            <p className="text-gray-600">
              Complimentary delivery to your doorstep
            </p>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={'bg-white rounded-lg shadow-md overflow-hidden ' +
                (plan.popular ? 'ring-2 ring-yellow-500' : '')}
            >
              {plan.popular && (
                <div className="bg-yellow-500 text-white text-center py-2">
                  Most Popular
                </div>
              )}
              <div className="p-8">
                <h3 className="text-2xl font-semibold mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <p className="text-3xl font-bold text-yellow-500 mb-6">{plan.price}</p>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading || (userSubscription && userSubscription.subscription_plans.id === plan.id)}
                  className="w-full bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Processing...
                    </>
                  ) : userSubscription && userSubscription.subscription_plans.id === plan.id ? (
                    'Current Plan'
                  ) : (
                    'Subscribe Now'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-2">Can I pause my subscription?</h3>
              <p className="text-gray-600">
                Yes, you can pause or skip deliveries anytime through your account dashboard.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-2">What if I'm not satisfied?</h3>
              <p className="text-gray-600">
                We offer a 100% satisfaction guarantee. If you're not happy with your delivery,
                we'll replace it or refund your money.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;